import { LocalNotifications } from "@capacitor/local-notifications";
import { Preferences } from "@capacitor/preferences";
import { hashStringToInt } from "../../helpers/hash";
import {
  dailyAgendaMessages,
  comebackMessages,
  getRandomMessage,
} from "./engagementMessages";
import {
  getNotificationPreferences,
  isNotificationTypeEnabled,
  shiftOutOfQuietHours,
} from "../notificationPreferences";

// Notification IDs (deterministic so we can cancel them)
const DAILY_AGENDA_NOTIFICATION_ID = hashStringToInt("engagement-daily-agenda");
const COMEBACK_NOTIFICATION_ID = hashStringToInt("engagement-comeback");

const PREFERENCES_KEY_DAILY = "engagement_daily_scheduled";
const PREFERENCES_KEY_COMEBACK = "engagement_comeback_scheduled";

/**
 * Get tomorrow at 12:00 PM in the user's local timezone.
 */
const getTomorrowNoon = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(12, 0, 0, 0);
  return tomorrow;
};

/**
 * Get 3 days from now at 10:00 AM in the user's local timezone.
 */
const getThreeDaysFromNow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 3);
  date.setHours(10, 0, 0, 0);
  return date;
};

/**
 * Cancel any previously scheduled engagement notifications.
 */
const cancelEngagementNotifications = async () => {
  try {
    await LocalNotifications.cancel({
      notifications: [
        { id: DAILY_AGENDA_NOTIFICATION_ID },
        { id: COMEBACK_NOTIFICATION_ID },
      ],
    });

    await Preferences.remove({ key: PREFERENCES_KEY_DAILY });
    await Preferences.remove({ key: PREFERENCES_KEY_COMEBACK });

    console.log("Cancelled existing engagement notifications");
  } catch (error) {
    console.warn("Failed to cancel engagement notifications:", error.message);
  }
};

/**
 * Schedule the daily agenda notification for tomorrow at 12 PM.
 *
 * This creates a "chain" — if the user opens the app today, they get
 * a friendly nudge tomorrow. If they don't open the app, no new
 * notification gets scheduled, so the chain breaks naturally.
 */
const scheduleDailyAgendaNotification = async (quietHours) => {
  try {
    const tomorrowNoon = getTomorrowNoon();
    const scheduledTime = shiftOutOfQuietHours(tomorrowNoon, quietHours);
    const message = getRandomMessage(dailyAgendaMessages);

    await LocalNotifications.schedule({
      notifications: [
        {
          title: message.title,
          body: message.body,
          id: DAILY_AGENDA_NOTIFICATION_ID,
          schedule: { at: scheduledTime, allowWhileIdle: true },
          channelId: "engagement_notifications",
        },
      ],
    });

    await Preferences.set({
      key: PREFERENCES_KEY_DAILY,
      value: JSON.stringify({
        scheduledAt: new Date().toISOString(),
        notificationId: DAILY_AGENDA_NOTIFICATION_ID,
      }),
    });

    console.log(
      "Scheduled daily agenda notification for:",
      tomorrowNoon.toISOString(),
    );
  } catch (error) {
    console.warn(
      "Failed to schedule daily agenda notification:",
      error.message,
    );
  }
};

/**
 * Schedule the comeback notification for 3 days from now.
 *
 * Each time the user opens the app, this is cancelled and rescheduled.
 * If the user keeps opening the app, they'll never see this notification.
 * If they stop opening the app, after 3 days they'll get a gentle nudge.
 */
const scheduleComebackNotification = async (quietHours) => {
  try {
    const threeDaysLater = getThreeDaysFromNow();
    const scheduledTime = shiftOutOfQuietHours(threeDaysLater, quietHours);
    const message = getRandomMessage(comebackMessages);

    await LocalNotifications.schedule({
      notifications: [
        {
          title: message.title,
          body: message.body,
          id: COMEBACK_NOTIFICATION_ID,
          schedule: { at: scheduledTime, allowWhileIdle: true },
          channelId: "engagement_notifications",
        },
      ],
    });

    await Preferences.set({
      key: PREFERENCES_KEY_COMEBACK,
      value: JSON.stringify({
        scheduledAt: new Date().toISOString(),
        notificationId: COMEBACK_NOTIFICATION_ID,
      }),
    });

    console.log(
      "Scheduled comeback notification for:",
      threeDaysLater.toISOString(),
    );
  } catch (error) {
    console.warn("Failed to schedule comeback notification:", error.message);
  }
};

/**
 * Main function to set up engagement notifications on app open.
 *
 * Call this each time the app is opened / user authenticates.
 * It will:
 * 1. Cancel any existing engagement notifications
 * 2. Schedule a daily agenda notification for tomorrow at 12 PM
 * 3. Schedule a comeback notification for 3 days from now
 */
const scheduleEngagementNotifications = async () => {
  try {
    // Load user notification preferences
    const prefs = await getNotificationPreferences();

    // Always clear existing engagement notifications first
    await cancelEngagementNotifications();

    // Only schedule if the user has enabled these notification types
    const dailyAgendaEnabled = isNotificationTypeEnabled(
      prefs,
      "engagement",
      "dailyAgenda",
    );
    const comebackEnabled = isNotificationTypeEnabled(
      prefs,
      "engagement",
      "comeback",
    );

    if (dailyAgendaEnabled) {
      await scheduleDailyAgendaNotification(prefs.quietHours);
    }

    if (comebackEnabled) {
      await scheduleComebackNotification(prefs.quietHours);
    }

    console.log("Engagement notifications scheduled successfully");
  } catch (error) {
    console.warn("Failed to schedule engagement notifications:", error.message);
  }
};

export default scheduleEngagementNotifications;
